import theme from "../../styles/theme";
import moment from "moment";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import {
  StyledTableCell,
  StyledTableCellRow,
  TableTitle,
  NoDataMessage,
  tableContainerStyles,
  PointsContainer,
} from "../Common/TableStyles";
import { useDriverContext } from "../../contexts/driver";
import { getRootSeasonShortName } from "../../utils/helpers/season";
import ProvisionalMark from "../Elo/ProvisionalMark";
import EloRatingChange from "../Elo/EloRatingChange";

const RecentEvents = () => {
  const { recentEvents, isEventsLoading } = useDriverContext();

  // Format date using moment
  const formatDate = (dateString) => {
    try {
      return moment(dateString).format("DD.MM.YYYY");
    } catch (error) {
      console.error("Error formatting date:", error);
      return dateString;
    }
  };

  // Calculate total license points from all sessions
  const calculateSafetyRating = (event) => {
    let totalPoints = 0;

    if (event.qualiResult?.penalty) {
      totalPoints += event.qualiResult.penalty.licensePoints || 0;
    }

    if (event.sprintResult?.penalty) {
      totalPoints += event.sprintResult.penalty.licensePoints || 0;
    }

    if (event.raceResult?.penalty) {
      totalPoints += event.raceResult.penalty.licensePoints || 0;
    }

    return totalPoints === 0 ? "" : `+${totalPoints}`;
  };

  const getEventTeam = (event) => {
    if (event?.qualiResult?.team) {
      return event.qualiResult.team.name;
    }

    if (event?.sprintResult?.team) {
      return event.sprintResult.team.name;
    }

    if (event?.raceResult?.team) {
      return event.raceResult.team.name;
    }

    return "";
  };

  const getEventTeamColor = (event) => {
    if (event?.qualiResult?.team?.color) {
      return event.qualiResult.team.color;
    }

    if (event?.sprintResult?.team?.color) {
      return event.sprintResult.team.color;
    }

    if (event?.raceResult?.team?.color) {
      return event.raceResult.team.color;
    }

    return theme.colors.green;
  };

  const calculateSlickRating = (event) => {
    // Get new slick rating from race result
    const newSlickRating = event.raceResult?.elo?.newElo;

    if (!newSlickRating) {
      return "-";
    }

    // Calculate total change from sprint and race
    let totalChange = 0;
    let isProvisional = false;

    if (event.sprintResult?.elo?.eloChange) {
      totalChange += event.sprintResult.elo.eloChange;
      if (event.sprintResult.elo.provisionalChange) {
        isProvisional = true;
      }
    }

    if (event.raceResult?.elo?.eloChange) {
      totalChange += event.raceResult.elo.eloChange;
      if (event.raceResult.elo.provisionalChange) {
        isProvisional = true;
      }
    }

    return (
      <>
        {newSlickRating} (
        <EloRatingChange change={totalChange} />){isProvisional && <ProvisionalMark />}
      </>
    );
  };

  if (isEventsLoading) {
    return (
      <TableContainer
        component={Paper}
        sx={{
          ...tableContainerStyles,
          marginBottom: "24px",
        }}
      >
        <TableTitle>Недавні етапи</TableTitle>
        <NoDataMessage>Завантаження даних...</NoDataMessage>
      </TableContainer>
    );
  }

  if (recentEvents.length === 0) {
    return (
      <TableContainer
        component={Paper}
        sx={{
          ...tableContainerStyles,
          marginBottom: "24px",
        }}
      >
        <TableTitle>Останні етапи</TableTitle>
        <NoDataMessage>Немає даних про останні етапи</NoDataMessage>
      </TableContainer>
    );
  }

  return (
    <TableContainer
      component={Paper}
      sx={{
        ...tableContainerStyles,
        marginBottom: "24px",
      }}
    >
      <TableTitle>Останні етапи</TableTitle>
      <Table sx={{ minWidth: 650 }} aria-label="recent events table">
        <TableHead>
          <TableRow>
            <StyledTableCell align="left">Дата</StyledTableCell>
            <StyledTableCell align="left">Етап</StyledTableCell>
            <StyledTableCell align="left">Команда</StyledTableCell>
            <StyledTableCell align="center">Кваліфікація</StyledTableCell>
            <StyledTableCell align="center">Спринт</StyledTableCell>
            <StyledTableCell align="center">Гонка</StyledTableCell>
            <StyledTableCell align="center">Safety Points</StyledTableCell>
            <StyledTableCell align="center">Slick Rating</StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {recentEvents.map((event, index) => (
            <TableRow key={index}>
              <StyledTableCellRow align="left">
                {formatDate(event.sessionDetails.date)}
              </StyledTableCellRow>
              <StyledTableCellRow align="left">{`${getRootSeasonShortName(event.sessionDetails.season)} - ${event.sessionDetails.shortName} - ${event.sessionDetails.car}`}</StyledTableCellRow>
              <StyledTableCellRow align="left">{`${getEventTeam(event)}`}</StyledTableCellRow>
              <StyledTableCellRow
                sx={{
                  color: `${getEventTeamColor(event)}`,
                }}
                align="center"
              >
                <PointsContainer align="center">
                  {event.qualiResult ? event.qualiResult?.finishPosition + "-й" : "-"}
                </PointsContainer>
              </StyledTableCellRow>

              <StyledTableCellRow
                sx={{
                  color: `${getEventTeamColor(event)}`,
                }}
                align="center"
              >
                <PointsContainer align="center">
                  {event.sprintResult ? event.sprintResult.finishPosition + "-й" : "-"}
                </PointsContainer>
              </StyledTableCellRow>
              <StyledTableCellRow
                sx={{
                  color: `${getEventTeamColor(event)}`,
                }}
                align="center"
              >
                <PointsContainer align="center">
                  {event.raceResult ? event.raceResult.finishPosition + "-й" : "-"}
                </PointsContainer>
              </StyledTableCellRow>
              <StyledTableCellRow
                sx={{
                  color: `${theme.colors.red}`,
                }}
                align="center"
              >
                {calculateSafetyRating(event)}
              </StyledTableCellRow>
              <StyledTableCellRow align="center">{calculateSlickRating(event)}</StyledTableCellRow>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default RecentEvents;
